import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Grid,
  Divider,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Statistic } from '@helpers/statistics.ts';
import { CloudDownload } from '@mui/icons-material';
import { useCallback } from 'react';
import { useDownload } from '@hooks/use-download.ts';

interface MetricsTableProps {
  metrics: Statistic | undefined;
}

export const MetricsTable = (props: MetricsTableProps) => {
  if (!props.metrics) return null;
  if (!props.metrics.zones) return null;

  const { downloadXlsx } = useDownload();
  const metrics = props.metrics;

  const onDownloadPlan = useCallback(() => {
    downloadXlsx(
      metrics.out_zones.map((x) => ({
        tracking_id: x,
      })),
      'ordenes-sin-zona.xlsx',
    );
  }, [metrics, downloadXlsx]);

  return (
    <Paper
      variant="outlined"
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: 'background.paper',
      }}
    >
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.light' }}>
              <TableCell sx={{ fontWeight: 'bold', color: 'primary.contrastText' }}>
                Zona
              </TableCell>
              <TableCell align="center" sx={{ color: 'primary.contrastText' }}>
                <Stack alignItems="center" spacing={0.5}>
                  <Typography variant="caption" fontWeight="bold">
                    Total
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    [{props.metrics.total ?? ''}]
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell align="center" sx={{ color: 'primary.contrastText' }}>
                <Stack alignItems="center" spacing={0.5}>
                  <Typography variant="caption" fontWeight="bold">
                    COD
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    [{props.metrics.total_cod ?? ''}]
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell align="center" sx={{ color: 'primary.contrastText' }}>
                <Stack alignItems="center" spacing={0.5}>
                  <Typography variant="caption" fontWeight="bold">
                    XS
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    [{props.metrics.total_xs ?? ''}]
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell align="center" sx={{ color: 'primary.contrastText' }}>
                <Stack alignItems="center" spacing={0.5}>
                  <Typography variant="caption" fontWeight="bold">
                    S
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    [{props.metrics.total_s ?? ''}]
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell align="center" sx={{ color: 'primary.contrastText' }}>
                <Stack alignItems="center" spacing={0.5}>
                  <Typography variant="caption" fontWeight="bold">
                    M
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    [{props.metrics.total_m ?? ''}]
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell align="center" sx={{ color: 'primary.contrastText' }}>
                <Stack alignItems="center" spacing={0.5}>
                  <Typography variant="caption" fontWeight="bold">
                    L
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    [{props.metrics.total_l ?? ''}]
                  </Typography>
                </Stack>
              </TableCell>
              <TableCell align="center" sx={{ color: 'primary.contrastText' }}>
                <Stack alignItems="center" spacing={0.5}>
                  <Typography variant="caption" fontWeight="bold">
                    XL
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    [{props.metrics.total_xl ?? ''}]
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {props.metrics.zones.map((metric) => (
              <TableRow
                hover
                key={`${metric.id}`}
                sx={{
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {metric.name}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    {metric.total}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{metric.metrics.cod}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{metric.metrics.xs}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{metric.metrics.s}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{metric.metrics.m}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{metric.metrics.l}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">{metric.metrics.xl}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Divider />
      <Grid
        container
        sx={{ p: 1.5, backgroundColor: 'grey.50' }}
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid component="div" size={{ xs: 'auto' }}>
          <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight="medium">
              {props.metrics.zones.length} Zonas
            </Typography>
            <Typography variant="body2" color="error" fontWeight="medium">
              {props.metrics.out_zones.length} paquetes fuera de zona
            </Typography>
          </Stack>
        </Grid>
        <Grid component="div" size={{ xs: 'auto' }}>
          <Tooltip title="Descargar listado de ordenes fuera de zona">
            <span>
              <IconButton
                onClick={onDownloadPlan}
                disabled={props.metrics.out_zones.length === 0}
                color="error"
                size="small"
              >
                <CloudDownload />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
    </Paper>
  );
};
